import React, { Dispatch, FC, SetStateAction, useState, useEffect } from "react";
import { Guest, GuestResponse, RsvpRequest, PlusOneRequest, PlusOnePatchRequest } from "../../../../types/Guests";
import Input from "../../../../components/form/Input";
import Checkbox from "../../../../components/form/Checkbox";
import "../../css/plus-one-manager.css";
import { toast } from "react-toastify";
import { API_URL, EVENT_NAME, USER_ID } from "../../../../constants/ApiConstants";

interface PlusOneManagerProps {
    guests: Guest[];
    rsvpMap: Map<number, RsvpRequest>;
    setPlusOneCompleted: Dispatch<SetStateAction<boolean>>;
    onPlusOnesCreated?: (newPlusOnes: Guest[]) => void;
}

interface PlusOneForm {
    id?: number; // Guest ID of the plus one (for existing plus ones)
    firstName: string;
    lastName: string;
    child: boolean;
    isExisting: boolean; // Track if this is an existing plus one or new
    originalFirstName?: string; // Original first name (for detecting changes)
    originalLastName?: string; // Original last name (for detecting changes)
}

const PlusOneManager: FC<PlusOneManagerProps> = ({ guests, rsvpMap, setPlusOneCompleted, onPlusOnesCreated }) => {
    const [plusOneForms, setPlusOneForms] = useState<Map<number, PlusOneForm[]>>(new Map());
    const [declinedPlusOnes, setDeclinedPlusOnes] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);

    const guestsWithPlusOnes = guests.filter(guest => {
        // Check both the rsvpMap (for new selections) and guest.status (for loaded data)
        const rsvpMapStatus = rsvpMap.get(guest.id)?.status;
        const isRsvping = rsvpMapStatus === "RSVP" || guest.status === "RSVP";
        return guest.plusOneCount && guest.plusOneCount > 0 && isRsvping;
    });

    // Fetch existing plus ones for each guest
    useEffect(() => {
        const fetchPlusOnes = async () => {
            if (guestsWithPlusOnes.length === 0) {
                setPlusOneCompleted(true);
                setLoading(false);
                return;
            }

            const newPlusOneForms = new Map<number, PlusOneForm[]>();
            const newDeclinedSet = new Set<number>();

            for (const guest of guestsWithPlusOnes) {
                try {
                    const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/guests/${guest.id}/plus-ones`);
                    const response = await fetch(url, { method: "GET" });

                    if (response.ok) {
                        const data: GuestResponse = await response.json();

                        if (data.items && data.items.length > 0) {
                            // Pre-populate forms with existing plus ones
                            const existingForms: PlusOneForm[] = data.items.map(plusOne => ({
                                id: plusOne.id,
                                firstName: plusOne.firstName,
                                lastName: plusOne.lastName,
                                child: plusOne.child || false,
                                isExisting: true,
                                originalFirstName: plusOne.firstName,
                                originalLastName: plusOne.lastName
                            }));
                            newPlusOneForms.set(guest.id, existingForms);

                            // Check if remaining slots were declined
                            if (guest.declinedPlusOnes && guest.declinedPlusOnes > 0) {
                                newDeclinedSet.add(guest.id);
                            }
                        }
                    } else if (response.status === 404) {
                        // No plus ones found for this guest - this is okay
                        console.log(`No existing plus ones for guest ${guest.id}`);

                        // Check if this guest has declined their plus ones
                        if (guest.declinedPlusOnes && guest.declinedPlusOnes > 0) {
                            newDeclinedSet.add(guest.id);
                        }
                    } else {
                        console.error(`Error fetching plus ones for guest ${guest.id}`);
                        toast.error(`Failed to load existing plus ones for ${guest.firstName} ${guest.lastName}`);
                    }
                } catch (error) {
                    console.error(`Error fetching plus ones for guest ${guest.id}:`, error);
                    toast.error(`Failed to load existing plus ones for ${guest.firstName} ${guest.lastName}`);
                }
            }

            setPlusOneForms(newPlusOneForms);
            setDeclinedPlusOnes(newDeclinedSet);
            setLoading(false);
        };

        fetchPlusOnes();
    }, []);

    useEffect(() => {
        // If no guests have plus ones, automatically proceed
        if (guestsWithPlusOnes.length === 0) {
            setPlusOneCompleted(true);
        }
    }, [guestsWithPlusOnes.length, setPlusOneCompleted]);

    const handleAddPlusOne = (guestId: number) => {
        const currentForms = plusOneForms.get(guestId) || [];
        const guest = guests.find(g => g.id === guestId);
        const maxPlusOnes = guest?.plusOneCount || 0;

        if (currentForms.length >= maxPlusOnes) {
            toast.error(`You can only add ${maxPlusOnes} plus one(s) for this guest`);
            return;
        }

        const newForms = [...currentForms, { firstName: "", lastName: "", child: false, isExisting: false }];
        setPlusOneForms(new Map(plusOneForms.set(guestId, newForms)));
    };

    const handleDeclineRemaining = (guestId: number) => {
        const newDeclined = new Set(declinedPlusOnes);
        newDeclined.add(guestId);
        setDeclinedPlusOnes(newDeclined);
        toast.info("Remaining plus ones declined for this guest");
    };

    const handleRevertDecline = (guestId: number) => {
        const newDeclined = new Set(declinedPlusOnes);
        newDeclined.delete(guestId);
        setDeclinedPlusOnes(newDeclined);
        toast.info("Decline reverted - you can now add more plus ones");
    };

    const handlePlusOneChange = (
        guestId: number,
        index: number,
        field: keyof PlusOneForm,
        value: string | boolean
    ) => {
        const currentForms = plusOneForms.get(guestId) || [];
        const updatedForms = [...currentForms];
        updatedForms[index] = { ...updatedForms[index], [field]: value };
        setPlusOneForms(new Map(plusOneForms.set(guestId, updatedForms)));
    };

    const handleRemovePlusOne = async (guestId: number, index: number) => {
        const currentForms = plusOneForms.get(guestId) || [];
        const plusOneToRemove = currentForms[index];

        // If this is an existing plus one, delete it from the backend
        if (plusOneToRemove.isExisting && plusOneToRemove.id) {

            try {
                const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/guests/${guestId}/plus-ones/${plusOneToRemove.id}`);
                const response = await fetch(url, { method: "DELETE" });

                if (!response.ok) {
                    toast.error(`Failed to delete ${plusOneToRemove.firstName} ${plusOneToRemove.lastName}`);
                    return;
                }

                toast.success(`${plusOneToRemove.firstName} ${plusOneToRemove.lastName} has been deleted`);
            } catch (error) {
                console.error("Error deleting plus one:", error);
                toast.error("Failed to delete plus one. Please try again.");
                return;
            }
        }

        // Remove from local state
        const updatedForms = currentForms.filter((_, i) => i !== index);
        setPlusOneForms(new Map(plusOneForms.set(guestId, updatedForms)));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Validate that all plus ones are either added or declined
        for (const guest of guestsWithPlusOnes) {
            const forms = plusOneForms.get(guest.id) || [];
            const isDeclined = declinedPlusOnes.has(guest.id);
            const maxPlusOnes = guest.plusOneCount || 0;

            if (!isDeclined && forms.length < maxPlusOnes) {
                // Check if all forms are filled
                const hasEmptyFields = forms.some(form => !form.firstName || !form.lastName);
                if (hasEmptyFields) {
                    toast.error("Please fill in all plus one names or decline remaining");
                    return;
                }

                toast.error(`Please add all plus ones for ${guest.firstName} ${guest.lastName} or decline remaining`);
                return;
            }

            // Validate that all added forms are complete
            if (forms.length > 0) {
                const hasEmptyFields = forms.some(form => !form.firstName.trim() || !form.lastName.trim());
                if (hasEmptyFields) {
                    toast.error("Please fill in all plus one names");
                    return;
                }
            }
        }

        // Send new plus ones to backend for each guest
        let totalNewPlusOnes = 0;
        const allCreatedPlusOnes: Guest[] = [];

        for (const guest of guestsWithPlusOnes) {
            const forms = plusOneForms.get(guest.id) || [];

            // Only send new plus ones (not existing ones)
            const newForms = forms.filter(form => !form.isExisting);

            if (newForms.length > 0) {
                const plusOneRequests: PlusOneRequest[] = newForms.map(form => ({
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    child: form.child
                }));

                try {
                    const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/guests/${guest.id}/plus-ones`);
                    const response = await fetch(url, {
                        method: "POST",
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(plusOneRequests)
                    });

                    if (!response.ok) {
                        toast.error(`Failed to save plus ones for ${guest.firstName} ${guest.lastName}. Please try again.`);
                        return;
                    }

                    // Get the created plus ones from the response (if any)
                    try {
                        const responseText = await response.text();
                        if (responseText) {
                            const createdPlusOnes: Guest[] = JSON.parse(responseText);
                            allCreatedPlusOnes.push(...createdPlusOnes);
                        }
                    } catch (jsonError) {
                        console.warn(`Response from plus ones POST was not JSON or empty, but request succeeded:`, jsonError);
                        // Continue anyway since the POST was successful
                    }

                    totalNewPlusOnes += newForms.length;
                } catch (error) {
                    console.error(`Error saving plus ones for guest ${guest.id}:`, error);
                    toast.error(`Failed to save plus ones for ${guest.firstName} ${guest.lastName}. Please try again.`);
                    return;
                }
            }
        }

        // Update existing plus ones if their names changed
        let totalUpdatedPlusOnes = 0;

        for (const guest of guestsWithPlusOnes) {
            const forms = plusOneForms.get(guest.id) || [];

            // Find existing plus ones whose names have changed
            const changedForms = forms.filter(form =>
                form.isExisting &&
                form.id &&
                (form.firstName.trim() !== form.originalFirstName ||
                 form.lastName.trim() !== form.originalLastName)
            );

            for (const form of changedForms) {
                const patchRequest: PlusOnePatchRequest = {
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim()
                };

                try {
                    const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/plus-ones/${form.id}`);
                    const response = await fetch(url, {
                        method: "PATCH",
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(patchRequest)
                    });

                    if (!response.ok) {
                        toast.error(`Failed to update ${form.originalFirstName} ${form.originalLastName}. Please try again.`);
                        return;
                    }

                    totalUpdatedPlusOnes++;
                } catch (error) {
                    console.error(`Error updating plus one ${form.id}:`, error);
                    toast.error(`Failed to update ${form.originalFirstName} ${form.originalLastName}. Please try again.`);
                    return;
                }
            }
        }

        // Show success messages
        if (totalNewPlusOnes > 0) {
            toast.success(`Successfully saved ${totalNewPlusOnes} plus one${totalNewPlusOnes > 1 ? 's' : ''}`);
        }
        if (totalUpdatedPlusOnes > 0) {
            toast.success(`Successfully updated ${totalUpdatedPlusOnes} plus one${totalUpdatedPlusOnes > 1 ? 's' : ''}`);
        }

        // Notify parent of newly created plus ones
        if (allCreatedPlusOnes.length > 0 && onPlusOnesCreated) {
            onPlusOnesCreated(allCreatedPlusOnes);
        }

        setPlusOneCompleted(true);
    };

    const getRemainingPlusOnes = (guestId: number): number => {
        const guest = guests.find(g => g.id === guestId);
        const maxPlusOnes = guest?.plusOneCount || 0;
        const currentForms = plusOneForms.get(guestId) || [];
        return maxPlusOnes - currentForms.length;
    };

    if (guestsWithPlusOnes.length === 0) {
        return null;
    }

    if (loading) {
        return (
            <div className="plusone__container">
                <h2>Manage Your Plus Ones</h2>
                <p className="plusone__loading">Loading existing plus ones...</p>
            </div>
        );
    }

    return (
        <div className="plusone__container">
            <form onSubmit={handleSubmit}>
                <h2>Manage Your Plus Ones</h2>
                {guestsWithPlusOnes.map((guest) => {
                    const forms = plusOneForms.get(guest.id) || [];
                    const isDeclined = declinedPlusOnes.has(guest.id);
                    const remaining = getRemainingPlusOnes(guest.id);
                    const maxPlusOnes = guest.plusOneCount || 0;

                    return (
                        <div key={guest.id} className="plusone__guest-section">
                            <h3>{guest.firstName} {guest.lastName}</h3>
                            <p className="plusone__allocation">
                                You have been given {maxPlusOnes} plus one{maxPlusOnes > 1 ? 's' : ''}
                            </p>

                            {/* Always show added forms, regardless of decline status */}
                            {forms.map((form, index) => (
                                <div key={index} className="plusone__form">
                                    <div className="plusone__form-fields">
                                        <Input
                                            type="text"
                                            name={`firstName-${guest.id}-${index}`}
                                            title="First Name"
                                            placeholder="First Name"
                                            value={form.firstName}
                                            onChange={(e) =>
                                                handlePlusOneChange(guest.id, index, "firstName", e.target.value)
                                            }
                                        />
                                        <Input
                                            type="text"
                                            name={`lastName-${guest.id}-${index}`}
                                            title="Last Name"
                                            placeholder="Last Name"
                                            value={form.lastName}
                                            onChange={(e) =>
                                                handlePlusOneChange(guest.id, index, "lastName", e.target.value)
                                            }
                                        />
                                        <Checkbox
                                            name={`child-${guest.id}-${index}`}
                                            title="Is this a child? (under 13)"
                                            value={form.child ? "true" : "false"}
                                            checked={form.child}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handlePlusOneChange(guest.id, index, "child", e.target.checked)
                                            }
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="plusone__btn-remove"
                                        onClick={() => handleRemovePlusOne(guest.id, index)}
                                    >
                                        {form.isExisting ? "Delete" : "Remove"}
                                    </button>
                                </div>
                            ))}

                            {/* Show add/decline buttons only if not declined */}
                            {!isDeclined && remaining > 0 && (
                                <div className="plusone__actions">
                                    <button
                                        type="button"
                                        className="plusone__btn"
                                        onClick={() => handleAddPlusOne(guest.id)}
                                    >
                                        Add New Plus One ({remaining} remaining)
                                    </button>
                                    <button
                                        type="button"
                                        className="plusone__btn plusone__btn-decline"
                                        onClick={() => handleDeclineRemaining(guest.id)}
                                    >
                                        Decline Remaining
                                    </button>
                                </div>
                            )}

                            {/* Show complete message or declined status */}
                            {!isDeclined && remaining === 0 && forms.length === maxPlusOnes && (
                                <p className="plusone__complete">All plus ones added</p>
                            )}

                            {isDeclined && remaining > 0 && (
                                <div className="plusone__declined-section">
                                    <p className="plusone__declined">
                                        Remaining plus one{remaining > 1 ? 's' : ''} declined ({remaining} guest{remaining > 1 ? 's' : ''})
                                    </p>
                                    <button
                                        type="button"
                                        className="plusone__btn plusone__btn-revert"
                                        onClick={() => handleRevertDecline(guest.id)}
                                    >
                                        Undo
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}

                <Input
                    type="submit"
                    name="submitPlusOnes"
                    value="Next"
                />
            </form>
        </div>
    );
};

export default PlusOneManager;
